import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Offcanvas } from 'bootstrap';
import { forkJoin, Observable, of } from 'rxjs';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import { UserInfo } from '../../models/users.model';
import {
  ChecklistItem,
  Task,
  TaskLinkPrefill,
  TASK_CATEGORY_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  TASK_TYPE_OPTIONS,
} from '../task.model';
import { LazyPicker, leadPicker, propertyPicker, staffPicker } from '../lazy-picker';

type LinkType = 'none' | 'property' | 'lead';

let formInstanceCounter = 0;

/**
 * Create / edit task offcanvas. Embedded by the tasks list, the task view and
 * (through the list) the property / lead pages. Open it with `open(taskId?)`.
 */
@Component({
  selector: 'app-task-form',
  standalone: true,
  imports: [NgIf, NgFor, FormsModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.css'],
})
export class TaskFormComponent implements AfterViewInit {
  @ViewChild('taskModal') taskModalElement!: ElementRef;
  /** Unique suffix so radio groups don't clash when two forms share a page. */
  readonly uid = `tf${++formInstanceCounter}`;
  private modal: Offcanvas | null = null;

  /** Link pre-filled on create (property page / lead page). */
  @Input() prefill: TaskLinkPrefill | null = null;
  @Output() saved = new EventEmitter<Task>();

  show = false;
  isEditMode = false;
  selectedTaskId = '';
  selectedTaskCode = '';
  taskForm!: FormGroup;
  linkType: LinkType = 'none';

  typeOptions = TASK_TYPE_OPTIONS;
  priorityOptions = TASK_PRIORITY_OPTIONS;
  statusOptions = TASK_STATUS_OPTIONS;
  categoryOptions = TASK_CATEGORY_OPTIONS;
  showLegacyCategory = false;

  users: LazyPicker;
  properties: LazyPicker;
  leads: LazyPicker;

  canAssign = false;
  currentUser: UserInfo | null = null;

  // Checklist (labels; existing items keep their id so done-state survives)
  checklist: { id?: string; label: string }[] = [];
  newChecklistLabel = '';
  private checklistDirty = false;

  // Attachments — 20MB per file; images / PDF / DOCX / CSV / Excel.
  selectedFiles: File[] = [];
  existingUploads: any[] = [];
  readonly maxFileSizeMb = 20;
  readonly acceptAttr = 'image/*,.pdf,.doc,.docx,.csv,.xls,.xlsx';
  private readonly allowedExt = ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'doc', 'docx', 'csv', 'xls', 'xlsx'];

  constructor(
    private fb: FormBuilder,
    private httpService: HttpServiceService,
    public loadingService: LoadingService,
    private localStorageService: LocalStorageService,
    private alertService: NgxToastrService,
    private destroyRef: DestroyRef,
  ) {
    this.canAssign = this.localStorageService.checkPermission(USER_PERMISSIONS.TASKS.TASKS_ASSIGN);
    this.currentUser = this.localStorageService.getItem<UserInfo>('userInfo');
    this.users = staffPicker(httpService, destroyRef);
    this.properties = propertyPicker(httpService, destroyRef);
    this.leads = leadPicker(httpService, destroyRef);
    this.initForm();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const el = this.taskModalElement?.nativeElement;
      if (el) {
        this.modal = Offcanvas.getOrCreateInstance(el, { backdrop: 'static', keyboard: false });
      }
    });
  }

  private initForm(): void {
    this.taskForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      task_type: [null],
      category: [null],
      priority: ['Medium'],
      status: [null],
      assignee_id: [null],
      property_id: [null],
      related_id: [null],
      due_date: [''],
      planned_start: [''],
      planned_end: [''],
      location_text: [''],
    });
  }

  private currentUserItem() {
    if (!this.currentUser?.id) return null;
    return {
      value: this.currentUser.id,
      label: `${this.currentUser.first_name ?? ''} ${this.currentUser.last_name ?? ''}`.trim(),
      data: this.currentUser,
    };
  }

  /** Open the panel: create mode (optional prefilled link) or edit mode. */
  open(taskId?: string): void {
    this.taskForm.reset({ priority: 'Medium' });
    this.checklist = [];
    this.checklistDirty = false;
    this.newChecklistLabel = '';
    this.selectedFiles = [];
    this.existingUploads = [];
    this.linkType = 'none';
    this.showLegacyCategory = false;
    this.users.init();
    this.properties.init();
    this.leads.init();
    this.users.ensure(this.currentUserItem());

    if (taskId) {
      this.isEditMode = true;
      this.selectedTaskId = taskId;
      this.loadTask(taskId);
    } else {
      this.isEditMode = false;
      this.selectedTaskId = '';
      this.selectedTaskCode = '';
      this.taskForm.patchValue({ assignee_id: this.currentUser?.id ?? null });
      this.applyPrefill();
    }
    this.applyAssignRule();
    this.show = true;
    this.modal?.show();
  }

  close(): void {
    this.show = false;
    this.modal?.hide();
  }

  private applyPrefill(): void {
    const p = this.prefill;
    if (!p) return;
    if (p.property_id) {
      this.linkType = 'property';
      this.properties.ensure({ value: p.property_id, label: p.property_name ?? 'Property' });
      this.taskForm.patchValue({ property_id: p.property_id });
    } else if (p.related_type === 'lead' && p.related_id) {
      this.linkType = 'lead';
      this.leads.ensure({ value: p.related_id, label: p.related_name ?? 'Lead' });
      this.taskForm.patchValue({ related_id: p.related_id });
    }
  }

  /** Without tasks_assign the assignee is always the caller (server rule). */
  private applyAssignRule(): void {
    const ctrl = this.taskForm.get('assignee_id');
    if (this.canAssign) ctrl?.enable({ emitEvent: false });
    else ctrl?.disable({ emitEvent: false });
  }

  setLinkType(type: LinkType): void {
    this.linkType = type;
    if (type !== 'property') this.taskForm.patchValue({ property_id: null });
    if (type !== 'lead') this.taskForm.patchValue({ related_id: null });
  }

  private loadTask(id: string): void {
    this.loadingService.show();
    forkJoin({
      task: this.httpService.getTaskDetailsById(id),
      checklist: this.httpService.getTaskChecklist(id),
    }).subscribe({
      next: ({ task, checklist }: any) => {
        this.loadingService.hide();
        if (!task?.success) return;
        const t: Task = task.data;
        this.selectedTaskCode = t.task_id || '';
        this.existingUploads = (t.uploads ?? []).filter((u) => (u.kind ?? 'attachment') === 'attachment');
        this.showLegacyCategory = !!t.category && !t.task_type;

        const assignee = t.assignee ?? t.owner;
        if (assignee?.id) this.users.ensure({ value: assignee.id, label: assignee.name, data: assignee });
        if (t.property?.id) {
          this.linkType = 'property';
          this.properties.ensure({ value: t.property.id, label: t.property.name, data: t.property });
        } else if (t.related?.type === 'lead' && t.related.id) {
          this.linkType = 'lead';
          this.leads.ensure({ value: t.related.id, label: t.related.name, data: t.related });
        }

        this.taskForm.patchValue({
          title: t.title,
          description: t.description ?? '',
          task_type: t.task_type ?? null,
          category: t.category ?? null,
          priority: t.priority ?? 'Medium',
          status: t.status ?? null,
          assignee_id: assignee?.id ?? null,
          property_id: t.property?.id ?? null,
          related_id: t.related?.type === 'lead' ? t.related.id : null,
          due_date: t.due_date ?? '',
          planned_start: t.planned_start ?? '',
          planned_end: t.planned_end ?? '',
          location_text: t.location_text ?? '',
        });

        const items: ChecklistItem[] = checklist?.success ? checklist.data ?? [] : [];
        this.checklist = items.map((i) => ({ id: i.id, label: i.label }));
      },
      error: () => this.loadingService.hide(),
    });
  }

  // ── Checklist ──────────────────────────────────────────────────────────
  addChecklistItem(): void {
    const label = this.newChecklistLabel.trim();
    if (!label) return;
    this.checklist.push({ label });
    this.newChecklistLabel = '';
    this.checklistDirty = true;
  }

  removeChecklistItem(index: number): void {
    this.checklist.splice(index, 1);
    this.checklistDirty = true;
  }

  onChecklistLabelChange(): void {
    this.checklistDirty = true;
  }

  // ── Attachments ────────────────────────────────────────────────────────
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const maxBytes = this.maxFileSizeMb * 1024 * 1024;
    Array.from(input.files).forEach((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!this.allowedExt.includes(ext) && !file.type.startsWith('image/')) {
        this.alertService.error(`"${file.name}" is not an allowed file type`, 'toast-top-right');
        return;
      }
      if (file.size > maxBytes) {
        this.alertService.error(`"${file.name}" exceeds the ${this.maxFileSizeMb}MB limit`, 'toast-top-right');
        return;
      }
      this.selectedFiles.push(file);
    });
    input.value = '';
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  deleteExistingUpload(uid: string): void {
    if (!confirm('Delete this attachment?')) return;
    this.loadingService.show();
    this.httpService.deleteTaskUpload(uid).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.existingUploads = this.existingUploads.filter((u) => u.id !== uid);
          this.alertService.success(response.message, 'toast-top-right');
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  // ── Submit ─────────────────────────────────────────────────────────────
  isFieldInvalid(name: string): boolean {
    const c = this.taskForm.get(name);
    return !!(c && c.invalid && c.touched);
  }

  onSubmit(): void {
    if (this.taskForm.invalid) {
      Object.values(this.taskForm.controls).forEach((c) => c.markAsTouched());
      return;
    }
    const raw = this.taskForm.getRawValue();
    const body: Record<string, any> = {
      title: raw.title,
      description: raw.description || '',
      task_type: raw.task_type || null,
      priority: raw.priority || null,
      due_date: raw.due_date || null,
      planned_start: raw.planned_start || null,
      planned_end: raw.planned_end || null,
      location_text: raw.location_text || '',
    };
    if (this.showLegacyCategory && raw.category) body['category'] = raw.category;
    if (this.canAssign && raw.assignee_id) body['assignee_id'] = raw.assignee_id;
    if (this.isEditMode && raw.status) body['status'] = raw.status;

    // Link — send empty values on edit so the server can clear a removed link.
    body['property_id'] = this.linkType === 'property' ? raw.property_id || null : null;
    if (this.linkType === 'lead' && raw.related_id) {
      body['related_type'] = 'lead';
      body['related_id'] = raw.related_id;
    } else {
      body['related_type'] = null;
      body['related_id'] = null;
    }
    if (!this.isEditMode) body['checklist'] = this.checklist.map((c) => c.label);

    const payload: any = this.selectedFiles.length > 0 ? this.toFormData(body) : this.stripNulls(body);
    this.loadingService.show();
    const call = this.isEditMode
      ? this.httpService.updateTask(this.selectedTaskId, payload)
      : this.httpService.createTask(payload);

    call.subscribe({
      next: (response: any) => {
        if (!response.success) {
          this.loadingService.hide();
          return;
        }
        const task: Task = response.data;
        const taskId = this.isEditMode ? this.selectedTaskId : task?.id;
        // Edit: the checklist is replaced through its own endpoint so done state is kept.
        const checklistCall: Observable<any> =
          this.isEditMode && this.checklistDirty && taskId
            ? this.httpService.updateTaskChecklist(taskId, { items: this.checklist })
            : of(null);
        checklistCall.subscribe({
          next: () => {
            this.loadingService.hide();
            this.alertService.success(response.message, 'toast-top-right');
            this.close();
            this.saved.emit(task);
          },
          error: () => {
            this.loadingService.hide();
            this.close();
            this.saved.emit(task);
          },
        });
      },
      error: () => this.loadingService.hide(),
    });
  }

  /** JSON body: drop nulls on create (server defaults apply); keep them on edit to clear fields. */
  private stripNulls(body: Record<string, any>): Record<string, any> {
    if (this.isEditMode) return body;
    const out: Record<string, any> = {};
    Object.keys(body).forEach((k) => {
      if (body[k] !== null && body[k] !== undefined && body[k] !== '') out[k] = body[k];
    });
    return out;
  }

  private toFormData(body: Record<string, any>): FormData {
    const fd = new FormData();
    Object.keys(body).forEach((k) => {
      const v = body[k];
      if (v === null || v === undefined) {
        if (this.isEditMode) fd.append(k, '');
        return;
      }
      if (Array.isArray(v)) fd.append(k, JSON.stringify(v));
      else if (v !== '') fd.append(k, v);
    });
    // Field name must match the server upload config (TASK.UPLOADS.name = 'files').
    this.selectedFiles.forEach((f) => fd.append('files', f));
    return fd;
  }
}
